import { ReviewRequestType } from "./types";

export type FieldKind = "text" | "textarea" | "url" | "email" | "date" | "select";

export interface SelectOption {
  value: string;
  label: string;
}

export interface FieldConfig {
  name: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  requiredMessage?: string;
  minLength?: number;
  minLengthMessage?: string;
  maxLength?: number;
  maxLengthMessage?: string;
  invalidMessage?: string;
  helperText?: string;
  disabled?: boolean;
  options?: SelectOption[];
  rows?: number;
}

const FIELD_PRIMARY_CONTACT: FieldConfig = {
  name: "primaryContact",
  label: "Primary contact person for the app review",
  kind: "email",
  required: true,
  disabled: true,
};

const FIELD_GO_LIVE_DATE: FieldConfig = {
  name: "projectedGoLiveDate",
  label: "Projected go-live date",
  kind: "date",
  required: true,
  requiredMessage: "Please select the projected go-live date.",
};

const FIELD_SCOPE: FieldConfig = {
  name: "scope",
  label: "Describe the scope of the application to be reviewed",
  kind: "textarea",
  required: true,
  requiredMessage: "Please provide a detailed application scope with a minimum of 50 characters.",
  rows: 4,
  minLength: 50,
  minLengthMessage: "Please provide a detailed application scope with a minimum of 50 characters.",
  maxLength: 2000,
  maxLengthMessage: "Application scope cannot exceed 2000 characters.",
};

const FIELD_SECURITY_REPORT: FieldConfig = {
  name: "securityReportLink",
  label: "Latest security review report link",
  kind: "url",
  required: true,
  requiredMessage: "Please provide a valid security review report link.",
  invalidMessage: "Please provide a valid security review report link.",
};

const FIELD_DPIA: FieldConfig = {
  name: "dpiaLink",
  label: "If handling or Processing PII, please attach link to the completed Data Protection Impact Assessment (DPIA) document",
  kind: "url",
  required: false,
};

// const FIELD_DRIVE_LINK: FieldConfig = {
//   name: "driveFolderLink",
//   label: "Google Drive Folder Link",
//   kind: "url",
//   required: false,
//   helperText: "Upload link to folder containing checklist items",
// };

const PRODUCTION_AUTH_QUESTIONS: FieldConfig[] = [
  {
    name: "userAuthMethod",
    label: "How do users authenticate to the application?",
    kind: "select",
    required: true,
    options: [
      { value: "sso_okta", label: "SSO via Okta" },
      { value: "oidc", label: "OIDC" },
      { value: "saml", label: "SAML" },
      { value: "ldap", label: "LDAP" },
      { value: "local", label: "Local accounts" },
      { value: "other", label: "Other" },
    ],
  },
  {
    name: "canDisableNonSso",
    label: "Can non-SSO/local login be disabled?",
    kind: "select",
    required: true,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "na", label: "Not applicable" },
    ],
  },
  {
    name: "userProvisioning",
    label: "How are users provisioned and deprovisioned?",
    kind: "select",
    required: true,
    options: [
      { value: "scim", label: "Automated via SCIM" },
      { value: "manual", label: "Manual via admin console" },
      { value: "jit", label: "Just-in-time" },
      { value: "other", label: "Other" },
    ],
  },
  {
    name: "sessionRevocation",
    label: "Does the application support session revocation and configurable session timeout?",
    kind: "select",
    required: true,
    options: [
      { value: "both", label: "Yes - both" },
      { value: "revocation_only", label: "Only revocation" },
      { value: "timeout_only", label: "Only timeout" },
      { value: "none", label: "No" },
    ],
  },
];

export interface ReviewRequestFormSchema {
  reviewTypeLabel: string;
  fields: FieldConfig[];
  showConfirmation: boolean;
}

export const REVIEW_REQUEST_FORM_SCHEMAS: Record<ReviewRequestType, ReviewRequestFormSchema> = {
  [ReviewRequestType.SIGNIFICANT_ENHANCEMENT]: {
    reviewTypeLabel: "Significant Enhancement",
    showConfirmation: true,
    fields: [
      FIELD_PRIMARY_CONTACT,
      FIELD_GO_LIVE_DATE,
      FIELD_SCOPE,
      FIELD_SECURITY_REPORT,
      FIELD_DPIA,
      // FIELD_DRIVE_LINK,
    ],
  },
  [ReviewRequestType.APPLICATION_INTEGRATION]: {
    reviewTypeLabel: "Application Integration",
    showConfirmation: true,
    fields: [
      FIELD_PRIMARY_CONTACT,
      FIELD_GO_LIVE_DATE,
      {
        name: "internalAppToIntegrate",
        label: "Which internal app will be integrated with this application?",
        kind: "text",
        required: true,
        requiredMessage: "Please provide a valid application name with at least 3 characters.",
        minLength: 3,
        minLengthMessage: "Please provide a valid application name with at least 3 characters.",
        maxLength: 100,
        maxLengthMessage: "Application name cannot exceed 100 characters.",
      },
      {
        name: "scope",
        label: "Describe the scope of the application that needs to be reviewed",
        kind: "textarea",
        required: true,
        requiredMessage: "Please provide a detailed application scope with a minimum of 50 characters.",
        rows: 4,
        minLength: 50,
        minLengthMessage: "Please provide a detailed application scope with a minimum of 50 characters.",
        maxLength: 2000,
        maxLengthMessage: "Application scope cannot exceed 2000 characters.",
      },
      FIELD_SECURITY_REPORT,
      FIELD_DPIA,
    ],
  },
  [ReviewRequestType.DATA_ACCESS]: {
    reviewTypeLabel: "Data Access",
    showConfirmation: true,
    fields: [
      FIELD_PRIMARY_CONTACT,
      FIELD_GO_LIVE_DATE,
      FIELD_SCOPE,
      FIELD_SECURITY_REPORT,
      FIELD_DPIA,
      // FIELD_DRIVE_LINK,
    ],
  },
  [ReviewRequestType.PRODUCTION_READINESS]: {
    reviewTypeLabel: "Production Readiness",
    showConfirmation: true,
    fields: [
      FIELD_PRIMARY_CONTACT,
      FIELD_GO_LIVE_DATE,
      FIELD_SCOPE,
      FIELD_DPIA,
      // FIELD_DRIVE_LINK,
      ...PRODUCTION_AUTH_QUESTIONS,
    ],
  },
};

export const CONFIRMATION_TEXT
  = "I confirm and acknowledge that the application general details is up to date.";

export const CONFIRMATION_HINT
  = "If you have not updated the details kindly go to the application details page and edit the details before submitting for review.";
